import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, User, X, Calendar, AlertCircle, CheckCircle, Home, Users, Stethoscope, BarChart3 } from 'lucide-react';

const HeaderContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;

  @media (min-width: 1024px) {
    padding: 12px 40px;
  }
`;

const Logo = styled.h1`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  letter-spacing: -0.3px;
  cursor: pointer;
`;

const DesktopNav = styled.nav`
  display: none;
  gap: 8px;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ $active, theme }) => $active ? theme.colors.info : 'transparent'};
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  }

  span {
    font-size: 14px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.info};
    
    svg, span {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const IconsContainer = styled.div`
  display: flex;
  gap: 12px;
  position: static;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  transition: all 0.3s ease;
  position: relative;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  background: ${({ theme }) => theme.colors.dangerText};
  color: white;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const NotificationsDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 20px;
  width: 320px;
  max-height: 450px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  z-index: 9999;
  opacity: ${({ $isVisible }) => $isVisible ? '1' : '0'};
  transform: ${({ $isVisible }) => $isVisible ? 'translateY(0)' : 'translateY(-10px)'};
  visibility: ${({ $isVisible }) => $isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const NotificationsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const NotificationsTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const NotificationsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  flex: 1;
`;

const NotificationItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${({ $unread, theme }) => $unread ? theme.colors.info : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $type, theme }) => {
    switch ($type) {
      case 'appointment':
        return theme.colors.info;
      case 'alert':
        return theme.colors.warning;
      case 'success':
        return theme.colors.success;
      default:
        return theme.colors.gray;
    }
  }};

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $type, theme }) => {
      switch ($type) {
        case 'appointment':
          return theme.colors.primary;
        case 'alert':
          return theme.colors.warningText;
        case 'success':
          return theme.colors.successText;
        default:
          return theme.colors.textSecondary;
      }
    }};
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
  line-height: 1.4;
`;

const NotificationTime = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyNotifications = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const MarkAllRead = styled.button`
  width: 100%;
  padding: 12px;
  background: ${({ theme }) => theme.colors.gray};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0 0 12px 12px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.info};
  }
`;

const UserIcon = styled(IconButton)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  width: 40px;
  height: 40px;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const MainHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef(null);

  const navItems = [
    { path: '/', icon: Home, label: 'Inicio' },
    { path: '/agenda', icon: Calendar, label: 'Agenda' },
    { path: '/pacientes', icon: Users, label: 'Pacientes' },
    { path: '/medicos', icon: Stethoscope, label: 'Médicos' },
    { path: '/reportes', icon: BarChart3, label: 'Reportes' },
  ];

  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'appointment',
      text: 'Nueva cita agendada con María García para mañana a las 10:00 AM',
      time: 'Hace 5 min',
      unread: true
    },
    {
      id: 2,
      type: 'alert',
      text: 'Recordatorio: Cita con Juan Pérez en 30 minutos',
      time: 'Hace 25 min',
      unread: true
    },
    {
      id: 3,
      type: 'success',
      text: 'Pago recibido de Ana López por consulta general',
      time: 'Hace 1 hora',
      unread: false
    },
    {
      id: 4,
      type: 'appointment',
      text: 'Cita cancelada: Carlos Ruiz - 15 de Diciembre',
      time: 'Hace 2 horas',
      unread: false
    }
  ]);

  const unreadCount = notifications.filter(n => n.unread).length;

  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return <Calendar />;
      case 'alert':
        return <AlertCircle />;
      case 'success':
        return <CheckCircle />;
      default:
        return <Bell />;
    }
  };

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  return (
    <HeaderContainer>
      <Logo onClick={() => navigate('/')}>Dr. Desk</Logo>
      
      <DesktopNav>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <NavItem
              key={item.path}
              $active={isActive}
              onClick={() => navigate(item.path)}
            >
              <Icon />
              <span>{item.label}</span>
            </NavItem>
          );
        })}
      </DesktopNav>

      <IconsContainer>
        <div ref={notificationsRef} style={{ position: 'relative' }}>
          <IconButton onClick={toggleNotifications}>
            <Bell />
            {unreadCount > 0 && <NotificationBadge>{unreadCount}</NotificationBadge>}
          </IconButton>
        </div>
        <UserIcon onClick={() => navigate('/perfil')}>
          <User size={20} />
        </UserIcon>
      </IconsContainer>
      
      <NotificationsDropdown $isVisible={showNotifications}>
        <NotificationsHeader>
          <NotificationsTitle>Notificaciones</NotificationsTitle>
          <CloseButton onClick={() => setShowNotifications(false)}>
            <X />
          </CloseButton>
        </NotificationsHeader>
        
        <NotificationsList>
          {notifications.length > 0 ? (
            notifications.map(notification => (
              <NotificationItem key={notification.id} $unread={notification.unread}>
                <NotificationIcon $type={notification.type}>
                  {getNotificationIcon(notification.type)}
                </NotificationIcon>
                <NotificationContent>
                  <NotificationText>{notification.text}</NotificationText>
                  <NotificationTime>{notification.time}</NotificationTime>
                </NotificationContent>
              </NotificationItem>
            ))
          ) : (
            <EmptyNotifications>
              <Bell />
              <p>No hay notificaciones</p>
            </EmptyNotifications>
          )}
        </NotificationsList>
        
        {notifications.length > 0 && unreadCount > 0 && (
          <MarkAllRead onClick={markAllAsRead}>
            Marcar todas como leídas
          </MarkAllRead>
        )}
      </NotificationsDropdown>
    </HeaderContainer>
  );
};

export default MainHeader;
